import React, { useState, useEffect } from 'react';
import { assignmentAPI, deviceAPI, usersAPI } from '../services/api';

const DeviceAssignment = () => {
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // New state for device history view
  const [viewMode, setViewMode] = useState('active'); // 'active', 'history', or 'recentEdits'
  const [selectedDevice, setSelectedDevice] = useState('');
  const [deviceHistory, setDeviceHistory] = useState(null);
  const [deviceEditHistory, setDeviceEditHistory] = useState(null);
  const [allRecentEdits, setAllRecentEdits] = useState(null);
  const [historyTab, setHistoryTab] = useState('assignments'); // 'assignments' or 'edits'
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingRecentEdits, setLoadingRecentEdits] = useState(false);
  const [deviceSearchTerm, setDeviceSearchTerm] = useState('');
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDeviceDropdown && !event.target.closest('.device-search-container')) {
        setShowDeviceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDeviceDropdown]);

  const loadData = async () => {
    try {
      const [devicesRes, usersRes, assignmentsRes] = await Promise.all([
        deviceAPI.getAll(),
        usersAPI.getDeviceManagers(),
        assignmentAPI.getAll()
      ]);
      
      setDevices(devicesRes.data);
      setUsers(usersRes.data);
      setAssignments(assignmentsRes.data);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to load data'
      });
    }
  };

  const loadAllRecentEdits = async () => {
    setLoadingRecentEdits(true);
    try {
      const response = await deviceAPI.getAllRecentEdits(1000);
      setAllRecentEdits(response.data);
      setMessage({ type: '', text: '' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to load recent edits'
      });
      setAllRecentEdits(null);
    } finally {
      setLoadingRecentEdits(false);
    }
  };

  const loadDeviceHistory = async (deviceId) => {
    if (!deviceId) {
      setDeviceHistory(null);
      setDeviceEditHistory(null);
      return;
    }

    setLoadingHistory(true);
    try {
      const [assignmentHistoryResponse, editHistoryResponse] = await Promise.all([
        assignmentAPI.getDeviceHistory(deviceId),
        deviceAPI.getEditHistory(deviceId)
      ]);
      
      setDeviceHistory(assignmentHistoryResponse.data);
      setDeviceEditHistory(editHistoryResponse.data);
      setMessage({ type: '', text: '' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to load history for device ${deviceId}`
      });
      setDeviceHistory(null);
      setDeviceEditHistory(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDeviceSelect = (device) => {
    setSelectedDevice(device.assetId);
    setDeviceSearchTerm(`${device.assetId} - ${device.deviceName || device.model || 'Unknown'} (${device.deviceType})`);
    setShowDeviceDropdown(false);
    loadDeviceHistory(device.assetId);
  };

  const handleClearSelection = () => {
    setSelectedDevice('');
    setDeviceSearchTerm('');
    setDeviceHistory(null);
    setDeviceEditHistory(null);
    setHistoryTab('assignments');
    setShowDeviceDropdown(false);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDeviceName = (assetId) => {
    const device = devices.find(d => d.assetId === assetId);
    if (!device) return 'Unknown';
    return device.deviceName || device.model || `${device.brand || ''} ${device.model || ''}`.trim() || 'Unknown';
  };

  const formatFieldName = (fieldName) => {
    // Convert camelCase to Title Case
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const getUserName = (userId) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.id === userId || u.email === userId);
    return user ? (user.name || user.email) : userId; // Show email as fallback instead of 'Unknown'
  };

  const getStatusBadge = (status, type) => {
    const statusColors = {
      active: '#10b981',
      inactive: '#6b7280',
      returned: '#f59e0b',
      pending: '#f59e0b',
      approved: '#10b981',
      rejected: '#ef4444',
      cancelled: '#6b7280'
    };
    
    const color = statusColors[status] || '#6b7280';
    
    return (
      <span style={{
        display: 'inline-block',
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '600',
        backgroundColor: `${color}20`,
        color: color,
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  const activeAssignments = assignments.filter(a => a.status === 'active');

  // Filter assignments based on search term
  const filteredAssignments = activeAssignments.filter(assignment => {
    if (!searchTerm) return true;
    const device = devices.find(d => d.assetId === assignment.deviceId);
    const assetId = device?.assetId || assignment.deviceId || '';
    return assetId.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Sort devices by asset ID for the dropdown
  const sortedDevices = [...devices].sort((a, b) => 
    (a.assetId || '').localeCompare(b.assetId || '')
  );

  // Filter devices based on device search term
  const filteredDevices = sortedDevices.filter(device => {
    if (!deviceSearchTerm) return true;
    const searchLower = deviceSearchTerm.toLowerCase();
    return (
      (device.assetId || '').toLowerCase().includes(searchLower) ||
      (device.deviceName || '').toLowerCase().includes(searchLower) ||
      (device.model || '').toLowerCase().includes(searchLower) ||
      (device.brand || '').toLowerCase().includes(searchLower) ||
      (device.deviceType || '').toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="card">
      <h2>History</h2>
      
      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* View Mode Toggle */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => {
              setViewMode('active');
              setSelectedDevice('');
              setDeviceHistory(null);
              setDeviceSearchTerm('');
            }}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              backgroundColor: viewMode === 'active' ? '#3b82f6' : 'white',
              color: viewMode === 'active' ? 'white' : '#1e293b',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            Active Assignments
          </button>
          <button
            onClick={() => setViewMode('history')}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              backgroundColor: viewMode === 'history' ? '#3b82f6' : 'white',
              color: viewMode === 'history' ? 'white' : '#1e293b',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            Device History
          </button>
          <button
            onClick={() => {
              setViewMode('recentEdits');
              loadAllRecentEdits();
            }}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              backgroundColor: viewMode === 'recentEdits' ? '#3b82f6' : 'white',
              color: viewMode === 'recentEdits' ? 'white' : '#1e293b',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            ✏️ Recent Changes
          </button>
        </div>
      </div>

      {/* Active Assignments View */}
      {viewMode === 'active' && (
        <>
          {/* Search Input */}
          <div style={{ marginBottom: '1.5rem' }}>
            <input
              type="text"
              placeholder="Search by Asset ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '0.75rem',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            />
            {searchTerm && (
              <span style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#64748b' }}>
                Found {filteredAssignments.length} result{filteredAssignments.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {activeAssignments.length === 0 ? (
            <p>No active assignments</p>
          ) : filteredAssignments.length === 0 ? (
            <p>No assignments found matching "{searchTerm}"</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Asset ID</th>
                    <th>Device</th>
                    <th>Device Type</th>
                    <th>Assigned By</th>
                    <th>Assigned To</th>
                    <th>Delivery Unit</th>
                    <th>Assigned Date & Time</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssignments.map(assignment => {
                    const device = devices.find(d => d.assetId === assignment.deviceId);
                    const user = users.find(u => u.id === (assignment.employeeId || assignment.userId) || u.email === (assignment.employeeId || assignment.userId));
                    const assignedByUser = assignment.assignedBy ? getUserName(assignment.assignedBy) : 'System';
                    return (
                      <tr key={assignment._id}>
                        <td style={{ fontWeight: '600' }}>{device?.assetId || 'N/A'}</td>
                        <td>{getDeviceName(assignment.deviceId)}</td>
                        <td>{device?.deviceType || device?.type || 'N/A'}</td>
                        <td>{assignedByUser}</td>
                        <td>{getUserName(assignment.employeeId || assignment.userId)}</td>
                        <td>{user?.deliveryUnit || device?.deliveryUnit || 'N/A'}</td>
                        <td>{formatDateTime(assignment.assignedAt)}</td>
                        <td>{assignment.notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Device History View */}
      {viewMode === 'history' && (
        <>
          {/* Device Selector with Autocomplete */}
          <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
              Select a device to view its complete assignment history:
            </label>
            
            {/* Search Input Container */}
            <div className="device-search-container" style={{ position: 'relative', maxWidth: '500px' }}>
              <input
                type="text"
                placeholder="Search by Asset ID, Name, Model, Brand, or Type..."
                value={deviceSearchTerm}
                onChange={(e) => {
                  setDeviceSearchTerm(e.target.value);
                  setShowDeviceDropdown(true);
                  if (!e.target.value) {
                    setSelectedDevice('');
                    setDeviceHistory(null);
                  }
                }}
                onFocus={() => setShowDeviceDropdown(true)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  paddingRight: selectedDevice ? '2.5rem' : '0.75rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
              />
              
              {/* Clear Button */}
              {selectedDevice && (
                <button
                  onClick={handleClearSelection}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                    fontSize: '1.25rem',
                    padding: '0.25rem',
                    lineHeight: 1
                  }}
                  title="Clear selection"
                >
                  ×
                </button>
              )}
              
              {/* Custom Dropdown List */}
              {showDeviceDropdown && deviceSearchTerm && !selectedDevice && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    backgroundColor: 'white',
                    border: '1px solid #cbd5e1',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000
                  }}
                >
                  {filteredDevices.length > 0 ? (
                    filteredDevices.map(device => (
                      <div
                        key={device.assetId}
                        onClick={() => handleDeviceSelect(device)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f1f5f9',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <div style={{ fontWeight: '600', color: '#1e293b' }}>
                          {device.assetId}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>
                          {device.deviceName || device.model || 'Unknown'} • {device.deviceType}
                          {device.brand && ` • ${device.brand}`}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                      No devices found matching "{deviceSearchTerm}"
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Search Results Count */}
            {deviceSearchTerm && showDeviceDropdown && !selectedDevice && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#64748b' }}>
                Found {filteredDevices.length} device{filteredDevices.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Loading State */}
          {loadingHistory && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              Loading history...
            </div>
          )}

          {/* Device History Display */}
          {!loadingHistory && selectedDevice && (deviceHistory || deviceEditHistory) && (
            <>
              {/* Device Info Card */}
              {deviceHistory && deviceHistory.device && (
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  border: '1px solid #e2e8f0'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1.1rem' }}>Device Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div>
                      <strong>Asset ID:</strong> {deviceHistory.device.assetId}
                    </div>
                    <div>
                      <strong>Name:</strong> {deviceHistory.device.deviceName || 'N/A'}
                    </div>
                    <div>
                      <strong>Type:</strong> {deviceHistory.device.deviceType}
                    </div>
                    <div>
                      <strong>Brand:</strong> {deviceHistory.device.brand || 'N/A'}
                    </div>
                    <div>
                      <strong>Model:</strong> {deviceHistory.device.model || 'N/A'}
                    </div>
                    <div>
                      <strong>Status:</strong> {getStatusBadge(deviceHistory.device.status, 'device')}
                    </div>
                    <div>
                      <strong>Delivery Unit:</strong> {deviceHistory.device.deliveryUnit || 'N/A'}
                    </div>
                  </div>
                </div>
              )}

              {/* Tabs for Assignment History and Edit History */}
              <div style={{ 
                marginBottom: '1.5rem',
                borderBottom: '2px solid #e2e8f0'
              }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setHistoryTab('assignments')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      borderBottom: historyTab === 'assignments' ? '3px solid #7c3aed' : '3px solid transparent',
                      backgroundColor: 'transparent',
                      color: historyTab === 'assignments' ? '#7c3aed' : '#64748b',
                      fontWeight: historyTab === 'assignments' ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    📋 Assignment History
                    {deviceHistory?.history?.length > 0 && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        backgroundColor: historyTab === 'assignments' ? '#7c3aed' : '#cbd5e1',
                        color: 'white',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem'
                      }}>
                        {deviceHistory.history.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setHistoryTab('edits')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      borderBottom: historyTab === 'edits' ? '3px solid #7c3aed' : '3px solid transparent',
                      backgroundColor: 'transparent',
                      color: historyTab === 'edits' ? '#7c3aed' : '#64748b',
                      fontWeight: historyTab === 'edits' ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    ✏️ Edit History
                    {deviceEditHistory?.length > 0 && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        backgroundColor: historyTab === 'edits' ? '#7c3aed' : '#cbd5e1',
                        color: 'white',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem'
                      }}>
                        {deviceEditHistory.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Assignment History Tab Content */}
              {historyTab === 'assignments' && (
                <>
                  {deviceHistory && deviceHistory.history && deviceHistory.history.length > 0 ? (
                    <div>
                      <h3 style={{ marginBottom: '1rem' }}>
                        Complete Assignment Timeline ({deviceHistory.history.length} record{deviceHistory.history.length !== 1 ? 's' : ''})
                      </h3>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Date & Time</th>
                              <th>Type</th>
                              <th>Status</th>
                              <th>From</th>
                              <th>To</th>
                              <th>Requested/Assigned By</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deviceHistory.history.map((record, index) => (
                              <tr key={`${record.id}-${index}`}>
                                <td>{formatDateTime(record.date)}</td>
                                <td style={{ textTransform: 'capitalize', fontWeight: '500' }}>
                                  {record.type}
                                </td>
                                <td>{getStatusBadge(record.status, record.type)}</td>
                                <td>
                                  {record.type === 'assignment' 
                                    ? '-'
                                    : (record.fromEmployeeName || 'Unassigned')}
                                </td>
                                <td>
                                  {record.type === 'assignment'
                                    ? record.employeeName
                                    : record.toEmployeeName}
                                </td>
                                <td>
                                  {record.type === 'assignment'
                                    ? record.assignedByName
                                    : record.requestedByName}
                                </td>
                                <td>{record.notes || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                      No assignment history found for this device.
                    </p>
                  )}
                </>
              )}

              {/* Edit History Tab Content */}
              {historyTab === 'edits' && (
                <>
                  {deviceEditHistory && deviceEditHistory.length > 0 ? (
                    <div>
                      <h3 style={{ marginBottom: '1rem' }}>
                        Device Edit History ({deviceEditHistory.length} edit{deviceEditHistory.length !== 1 ? 's' : ''})
                      </h3>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Date & Time</th>
                              <th>Edited By</th>
                              <th>Role</th>
                              <th>Fields Changed</th>
                              <th>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deviceEditHistory.map((record, index) => (
                              <tr key={`${record._id}-${index}`}>
                                <td>{formatDateTime(record.editedAt)}</td>
                                <td style={{ fontWeight: '500' }}>
                                  {record.editedByName || record.editedBy}
                                </td>
                                <td>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    backgroundColor: record.editedByRole === 'employee' ? '#fef3c7' : '#dbeafe',
                                    color: record.editedByRole === 'employee' ? '#92400e' : '#1e40af',
                                    textTransform: 'capitalize'
                                  }}>
                                    {record.editedByRole?.replace('_', ' ') || 'Unknown'}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ fontSize: '0.875rem' }}>
                                    {record.changedFields.map((field, idx) => (
                                      <span key={idx} style={{
                                        display: 'inline-block',
                                        marginRight: '0.5rem',
                                        marginBottom: '0.25rem',
                                        padding: '0.125rem 0.5rem',
                                        backgroundColor: '#f1f5f9',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem'
                                      }}>
                                        {formatFieldName(field)}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <details style={{ cursor: 'pointer' }}>
                                    <summary style={{ fontWeight: '500', color: '#7c3aed' }}>
                                      View Changes
                                    </summary>
                                    <div style={{ 
                                      marginTop: '0.5rem', 
                                      padding: '0.5rem',
                                      backgroundColor: '#f8fafc',
                                      borderRadius: '4px',
                                      fontSize: '0.875rem'
                                    }}>
                                      {Object.entries(record.changes).map(([field, change]) => (
                                        <div key={field} style={{ marginBottom: '0.5rem' }}>
                                          <strong>{formatFieldName(field)}:</strong>
                                          <div style={{ paddingLeft: '1rem', color: '#64748b' }}>
                                            <span style={{ color: '#dc2626' }}>❌ {String(change.oldValue || 'N/A')}</span>
                                            {' → '}
                                            <span style={{ color: '#16a34a' }}>✓ {String(change.newValue || 'N/A')}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                      No edit history found for this device.
                    </p>
                  )}
                </>
              )}
            </>
          )}

          {!loadingHistory && !selectedDevice && (
            <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
              Please select a device to view its assignment history.
            </p>
          )}
        </>
      )}

      {/* Recent Changes View - All Devices */}
      {viewMode === 'recentEdits' && (
        <>
          <div style={{
            backgroundColor: '#f8fafc',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '1.1rem' }}>
              ✏️ Recent Device Changes
            </h3>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>
              Showing the most recent device edits across all devices (up to 1000 records)
            </p>
          </div>

          {/* Loading State */}
          {loadingRecentEdits && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              Loading recent changes...
            </div>
          )}

          {/* Recent Edits Display */}
          {!loadingRecentEdits && allRecentEdits && allRecentEdits.length > 0 && (
            <div>
              <h3 style={{ marginBottom: '1rem' }}>
                Recent Device Edits ({allRecentEdits.length} edit{allRecentEdits.length !== 1 ? 's' : ''})
              </h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Device</th>
                      <th>Asset ID</th>
                      <th>Type</th>
                      <th>Edited By</th>
                      <th>Role</th>
                      <th>Fields Changed</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRecentEdits.map((record, index) => (
                      <tr key={`${record._id}-${index}`}>
                        <td>{formatDateTime(record.editedAt)}</td>
                        <td style={{ fontWeight: '500' }}>
                          {record.device ? (
                            <>
                              {record.device.deviceName || 'N/A'}
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {record.device.model}
                              </div>
                            </>
                          ) : (
                            <span style={{ color: '#64748b' }}>Device deleted</span>
                          )}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {record.deviceId}
                        </td>
                        <td>
                          {record.device && (
                            <span style={{
                              fontSize: '0.75rem',
                              color: '#64748b'
                            }}>
                              {record.device.deviceType}
                            </span>
                          )}
                        </td>
                        <td style={{ fontWeight: '500' }}>
                          {record.editedByName || record.editedBy}
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: record.editedByRole === 'employee' ? '#fef3c7' : '#dbeafe',
                            color: record.editedByRole === 'employee' ? '#92400e' : '#1e40af',
                            textTransform: 'capitalize'
                          }}>
                            {record.editedByRole?.replace('_', ' ') || 'Unknown'}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.875rem' }}>
                            {record.changedFields.map((field, idx) => (
                              <span key={idx} style={{
                                display: 'inline-block',
                                marginRight: '0.5rem',
                                marginBottom: '0.25rem',
                                padding: '0.125rem 0.5rem',
                                backgroundColor: '#f1f5f9',
                                borderRadius: '4px',
                                fontSize: '0.75rem'
                              }}>
                                {formatFieldName(field)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <details style={{ cursor: 'pointer' }}>
                            <summary style={{ fontWeight: '500', color: '#7c3aed' }}>
                              View Changes
                            </summary>
                            <div style={{ 
                              marginTop: '0.5rem', 
                              padding: '0.5rem',
                              backgroundColor: '#f8fafc',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              minWidth: '250px'
                            }}>
                              {Object.entries(record.changes).map(([field, change]) => (
                                <div key={field} style={{ marginBottom: '0.5rem' }}>
                                  <strong>{formatFieldName(field)}:</strong>
                                  <div style={{ paddingLeft: '1rem', color: '#64748b' }}>
                                    <span style={{ color: '#dc2626' }}>❌ {String(change.oldValue || 'N/A')}</span>
                                    {' → '}
                                    <span style={{ color: '#16a34a' }}>✓ {String(change.newValue || 'N/A')}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loadingRecentEdits && (!allRecentEdits || allRecentEdits.length === 0) && (
            <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
              No recent device edits found.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default DeviceAssignment;
